import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Minimize2, Maximize2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { ChatMessage } from "@/types";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Props {
  initialPrompt?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

const AIAssistant: React.FC<Props> = ({ initialPrompt, isOpen: initialIsOpen = false, onClose }) => {
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem('aiAssistant_isOpen');
    return saved ? JSON.parse(saved) : initialIsOpen;
  });
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('aiAssistant_isCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [language, setLanguage] = useState<"en" | "es">("en");
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "0",
      role: "assistant",
      content: "Hello! I'm your AI assistant. How can I help you today?",
      timestamp: Date.now()
    }
  ]);
  const [input, setInput] = useState(initialPrompt || "");
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const translations = {
    en: {
      title: "AI Companion",
      placeholder: "Ask me anything...",
      thinking: "Thinking...",
      welcomeMessage: "Hello! I'm your AI assistant. How can I help you today?",
      errorMessage: "I'm having trouble connecting right now. Please try again in a moment."
    },
    es: {
      title: "Asistente de IA",
      placeholder: "Pregúntame cualquier cosa...",
      thinking: "Pensando...",
      welcomeMessage: "¡Hola! Soy tu asistente de IA. ¿En qué puedo ayudarte hoy?",
      errorMessage: "Estoy teniendo problemas de conexión ahora. Por favor, inténtalo de nuevo en un momento."
    }
  };

  const t = translations[language];

  useEffect(() => {
    if (initialIsOpen !== undefined) {
      setIsOpen(initialIsOpen);
      localStorage.setItem('aiAssistant_isOpen', JSON.stringify(initialIsOpen));
    }
  }, [initialIsOpen]);

  useEffect(() => {
    localStorage.setItem('aiAssistant_isOpen', JSON.stringify(isOpen));
  }, [isOpen]);

  useEffect(() => {
    localStorage.setItem('aiAssistant_isCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    // Update welcome message when language changes
    setMessages(prev => {
      const updatedMessages = [...prev];
      if (updatedMessages[0] && updatedMessages[0].role === "assistant" && updatedMessages[0].id === "0") {
        updatedMessages[0] = {
          ...updatedMessages[0],
          content: t.welcomeMessage
        };
      }
      return updatedMessages;
    });
  }, [language]);

  useEffect(() => {
    if (initialPrompt) {
      setInput(initialPrompt);
      if (initialIsOpen) {
        // Trigger send after input is set
        setTimeout(() => handleSend(), 0);
      }
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    // Listen for custom event to open assistant
    const handleOpenAssistant = () => {
      setIsOpen(true);
      setIsCollapsed(false);
    };
    
    window.addEventListener("openAIAssistant", handleOpenAssistant);
    return () => window.removeEventListener("openAIAssistant", handleOpenAssistant);
  }, []);

  const toggleOpen = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (!newIsOpen && onClose) onClose();
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleSend = async () => {
    const textToSend = input.trim();
    if (!textToSend) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: textToSend,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke('event-assistant', {
        body: {
          messages: [
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: 'user', content: textToSend }
          ],
          language: language
        }
      });

      if (error) throw error;

      const responseText = data.message || "I'm sorry, I couldn't generate a response.";

      // Parse navigation directives
      const navDirectiveRegex = /\[NAV:(SECTION|PAGE):([^\]]+)\]/g;
      const matches = [...responseText.matchAll(navDirectiveRegex)];
      
      matches.forEach((match) => {
        const [, type, target] = match;
        if (type === 'PAGE') {
          // Navigate to page
          navigate(`/${target}`);
        } else if (type === 'SECTION') {
          // Smooth scroll to section
          const element = document.getElementById(target);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });

      // Remove navigation directives from display
      const cleanResponse = responseText.replace(/\[NAV:(SECTION|PAGE):[^\]]+\]/g, '').trim();

      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: cleanResponse,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error('Error calling assistant:', error);
      const errorMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: t.errorMessage,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={toggleOpen}
        className="fixed bottom-6 right-6 bg-primary hover:bg-primary/90 text-primary-foreground p-4 rounded-full shadow-lg transition-all z-50"
        aria-label="Open AI Assistant"
      >
        <MessageSquare size={24} />
      </button>
    );
  }

  if (isCollapsed) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={toggleCollapse}
          className="bg-card border border-border rounded-xl shadow-2xl p-3 hover:bg-accent transition-all"
          aria-label="Expand AI Assistant"
        >
          <div className="flex items-center gap-2">
            <MessageSquare size={20} className="text-primary" />
            <span className="text-sm font-semibold">{t.title}</span>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-card border border-border rounded-xl shadow-2xl flex flex-col z-50">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <MessageSquare size={20} className="text-primary" />
          <h3 className="font-semibold">{t.title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Select value={language} onValueChange={(value: "en" | "es") => setLanguage(value)}>
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="more" disabled>More languages...</SelectItem>
            </SelectContent>
          </Select>
          <button 
            onClick={toggleCollapse} 
            className="text-muted-foreground hover:text-foreground"
            aria-label="Minimize"
          >
            <Minimize2 size={20} />
          </button>
          <button 
            onClick={toggleOpen} 
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <ScrollArea className="flex-grow p-4">
        <div className="space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] p-3 rounded-lg ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {isProcessing && (
            <div className="flex justify-start">
              <div className="bg-muted text-muted-foreground p-3 rounded-lg">
                <p className="text-sm">{t.thinking}</p>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="p-4 border-t border-border">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t.placeholder}
            disabled={isProcessing}
            className="flex-grow"
          />
          <Button type="submit" disabled={isProcessing || !input.trim()} size="icon">
            <Send size={18} />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AIAssistant;
